import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui";
import Modal from "../../ui/UIElements/Modal";
import {
  useDriverManagerDetail,
  useToggleDriverManagerStatus,
  useDeleteDriverManager,
} from "../../../hooks/useDriverManagers";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import OverviewTab from "../../ui/Users/OverviewTab";
import DriverGroupTab from "../../ui/Users/DriverGroupTab";
import DeleteConfirmationModal from "../../ui/Modals/DeleteConfirmationModal";
import EditDriverManagerModal from "../../ui/Modals/EditDriverManagerModal";

const DriverManagerDetailPage = ({ initialUser = null }) => {
  const { t } = useTranslation("delivery");
  const { id } = useParams();
  const userId = parseInt(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const toggleUserStatusMutation = useToggleDriverManagerStatus();
  const deleteUserMutation = useDeleteDriverManager();

  // Get user data from navigation state or props
  const userFromState = location.state?.user;
  const userData = initialUser || userFromState;

  // Fetch user data
  const {
    data: currentUser,
    isLoading,
    error,
    isError,
    refetch: refetchUser,
  } = useDriverManagerDetail(userId, userData);

  const handleToggleUserStatus = () => {
    if (currentUser?.enabled) {
      setShowDeactivateModal(true);
    } else {
      handleConfirmToggle();
    }
  };

  const handleConfirmToggle = async () => {
    try {
      const newEnabledStatus = !currentUser.enabled;

      await toggleUserStatusMutation.mutateAsync({
        userId: currentUser.id,
        enabled: newEnabledStatus,
      });

      await refetchUser();

      setShowDeactivateModal(false);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUserMutation.mutateAsync(currentUser.id);
      navigate("/delivery/drivers-managers");
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleBackToDriversManagers = () => {
    navigate("/delivery/drivers-managers");
  };

  if (isLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center p-4">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500 sm:mb-4 sm:h-12 sm:w-12"></div>
          <p className="text-sm text-gray-400 sm:text-base">
            {t("driverManagerDetail.states.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !currentUser)) {
    return (
      <div className="px-4 py-8 text-center sm:py-12">
        <Icon
          name="UserX"
          className="mx-auto mb-3 h-8 w-8 text-gray-400 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="mt-2 text-sm font-medium text-gray-300 sm:text-base">
          {t("driverManagerDetail.states.notFoundTitle")}
        </h3>
        <p className="mt-1 text-xs text-gray-400 sm:text-sm">
          {error?.message ||
            t("driverManagerDetail.states.notFoundDescription")}
        </p>
        <button
          onClick={handleBackToDriversManagers}
          className="mt-3 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white transition-colors hover:bg-blue-700 sm:mt-4 sm:px-4 sm:text-base"
        >
          {t("driverManagerDetail.actions.backToDriversManagers")}
        </button>
      </div>
    );
  }

  const name =
    currentUser?.name && currentUser?.lastname
      ? `${currentUser.name} ${currentUser.lastname}`
      : currentUser?.name || t("driverManagerDetail.fallbacks.unknownUser");

  const initials =
    currentUser?.name && currentUser?.lastname
      ? `${currentUser.name.charAt(0)}${currentUser.lastname.charAt(
          0,
        )}`.toUpperCase()
      : currentUser?.name?.charAt(0)?.toUpperCase() || "U";

  const breadcrumbItems = [
    {
      label: t("driverManagerDetail.breadcrumb.delivery"),
      path: "/delivery/drivers-managers",
      icon: "Truck",
    },
    {
      label: name,
      active: true,
      disabled: true,
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* User Profile Header */}
      <div className="rounded-lg border border-gray-800 bg-gray-900">
        <div className="border-b border-gray-800 p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full sm:h-16 sm:w-16">
                {currentUser.photo ? (
                  <img
                    src={currentUser.photo}
                    alt={name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className={`flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 text-lg font-semibold text-white sm:text-2xl ${
                    currentUser.photo ? "hidden" : "flex"
                  }`}
                >
                  {initials}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-lg font-bold text-white sm:text-2xl">
                  {name}
                </h1>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex cursor-pointer items-center justify-center space-x-2 rounded-lg bg-blue-600 px-3 py-2 text-xs text-white transition-colors hover:bg-blue-700 sm:px-4 sm:text-sm"
                >
                  <Icon name="Edit" className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">
                    {t("common:actions.edit")}
                  </span>
                </button>
                <button
                  onClick={handleToggleUserStatus}
                  disabled={toggleUserStatusMutation.isPending}
                  className={`flex cursor-pointer items-center justify-center space-x-2 rounded-lg px-3 py-2 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm ${
                    currentUser.enabled
                      ? "bg-red-600 text-white hover:bg-red-700"
                      : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  <Icon
                    name={currentUser.enabled ? "UserX" : "UserCheck"}
                    className="h-3 w-3 sm:h-4 sm:w-4"
                  />
                  <span className="hidden sm:inline">
                    {toggleUserStatusMutation.isPending
                      ? t("driverManagerDetail.actions.updating")
                      : currentUser.enabled
                        ? t("driverManagerDetail.actions.deactivate")
                        : t("driverManagerDetail.actions.activate")}
                  </span>
                  <span className="sm:hidden">
                    {toggleUserStatusMutation.isPending
                      ? t("driverManagerDetail.actions.updating")
                      : currentUser.enabled
                        ? t("driverManagerDetail.actions.deactivate")
                        : t("driverManagerDetail.actions.activate")}
                  </span>
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  disabled={deleteUserMutation.isPending}
                  className="flex cursor-pointer items-center justify-center space-x-2 rounded-lg bg-red-600 px-3 py-2 text-xs text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm"
                >
                  <Icon name="Trash2" className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">
                    {t("driverManagerDetail.actions.delete")}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-800 px-4 sm:px-6">
          <nav className="flex space-x-4 overflow-x-auto sm:space-x-8">
            <button
              onClick={() => setActiveTab("overview")}
              className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                activeTab === "overview"
                  ? "border-blue-500 text-white"
                  : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
              }`}
            >
              {t("driverManagerDetail.tabs.overview")}
            </button>
            <button
              onClick={() => setActiveTab("driver-group")}
              className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                activeTab === "driver-group"
                  ? "border-blue-500 text-white"
                  : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
              }`}
            >
              {t("driverManagerDetail.tabs.driverGroups")}
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="p-4 sm:p-6">
          {/* Overview Tab */}
          <div className={activeTab === "overview" ? "block" : "hidden"}>
            <OverviewTab user={currentUser} />
          </div>

          {/* Driver Group Tab */}
          <div className={activeTab === "driver-group" ? "block" : "hidden"}>
            <DriverGroupTab userId={currentUser?.id} />
          </div>
        </div>
      </div>

      {/* Deactivate User Confirmation Modal */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title={t("driverManagerDetail.deactivateModal.title")}
        subtitle={t("driverManagerDetail.deactivateModal.subtitle")}
        maxWidth="max-w-md"
      >
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/20 sm:h-10 sm:w-10">
                <Icon
                  name="AlertTriangle"
                  className="h-4 w-4 text-red-400 sm:h-5 sm:w-5"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-medium text-white">
                {t("driverManagerDetail.deactivateModal.confirmTitle", {
                  name,
                })}
              </h3>
              <p className="mt-1 text-xs text-gray-400 sm:text-sm">
                {t("driverManagerDetail.deactivateModal.description")}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
            <button
              onClick={handleConfirmToggle}
              disabled={toggleUserStatusMutation.isPending}
              className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm"
            >
              {toggleUserStatusMutation.isPending ? (
                <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-white sm:h-4 sm:w-4"></div>
              ) : null}
              <span>
                {toggleUserStatusMutation.isPending
                  ? t("driverManagerDetail.deactivateModal.deactivating")
                  : t("driverManagerDetail.actions.deactivate")}
              </span>
            </button>
            <button
              onClick={() => setShowDeactivateModal(false)}
              disabled={toggleUserStatusMutation.isPending}
              className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-xs font-medium text-gray-300 transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm"
            >
              {t("common:actions.cancel")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t("driverManagerDetail.deleteModal.title")}
        itemName={name}
        itemType={t("driverManagerDetail.deleteModal.itemType")}
        isLoading={deleteUserMutation.isPending}
      />

      {/* Edit Driver Manager Modal */}
      <EditDriverManagerModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          refetchUser();
        }}
        user={currentUser}
      />
    </div>
  );
};

export default DriverManagerDetailPage;
